import { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../axios';
import { useUserProfile } from '../../hooks/useUserProfile';
import '../../styles/profile-page.css';

const MAX_UPLOAD_SIZE = 2 * 1024 * 1024;

const ProfilePage = () => {
    const { profile, refresh } = useUserProfile();
    const fileInputRef = useRef(null);
    const initializedRef = useRef(false);
    const objectUrlRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [form, setForm] = useState({
        name: '',
        email: '',
        phone_number: '',
        bio: '',
        address: '',
        city: '',
        province: '',
        postal_code: '',
    });

    useEffect(() => {
        if (!profile) return;
        if (initializedRef.current) return;

        initializedRef.current = true;

        setAvatarPreview(profile.avatar_url || '');
        setForm({
            name: profile.name || '',
            email: profile.email || '',
            phone_number: profile.phone_number || '',
            bio: profile.bio || '',
            address: profile.address || '',
            city: profile.city || '',
            province: profile.province || '',
            postal_code: profile.postal_code || '',
        });
    }, [profile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const compressImage = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const image = new Image();
                image.onload = async () => {
                    try {
                        let width = image.width;
                        let height = image.height;
                        const MAX_DIMENSION = 1200;
                        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                            if (width > height) {
                                height = (height * MAX_DIMENSION) / width;
                                width = MAX_DIMENSION;
                            } else {
                                width = (width * MAX_DIMENSION) / height;
                                height = MAX_DIMENSION;
                            }
                        }
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        canvas.getContext('2d').drawImage(image, 0, 0, width, height);

                        let quality = 0.9;
                        const createBlob = () =>
                            new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', quality));

                        let blob = await createBlob();
                        while (blob && blob.size > MAX_UPLOAD_SIZE && quality > 0.3) {
                            quality -= 0.1;
                            blob = await createBlob();
                        }
                        resolve(
                            new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                                type: 'image/jpeg',
                            })
                        );
                    } catch (err) {
                        reject(err);
                    }
                };
                image.onerror = reject;
                image.src = event.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const handleAvatarChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSaveError('');

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setSaveError('Format gambar harus JPG, JPEG, PNG atau WEBP');
            return;
        }

        try {
            let processedFile = file;
            if (file.size > MAX_UPLOAD_SIZE) {
                processedFile = await compressImage(file);
            }

            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }

            const newUrl = URL.createObjectURL(processedFile);
            objectUrlRef.current = newUrl;

            setAvatarFile(processedFile);
            setAvatarPreview(newUrl);
        } catch (err) {
            console.error(err);
            setSaveError('Gagal memproses gambar');
        }
    };

    const openFilePicker = () => {
        if (!fileInputRef.current) return;
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSaveSuccess(false);
        setSaveError('');

        try {
            const formData = new FormData();
            formData.append('name', form.name);
            formData.append('email', form.email);
            formData.append('bio', form.bio);
            formData.append('address', form.address);
            formData.append('city', form.city);
            formData.append('province', form.province);
            formData.append('postal_code', form.postal_code);
            if (avatarFile) formData.append('avatar', avatarFile);

            await axiosInstance.post('/profile/update', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setAvatarFile(null);
            if (objectUrlRef.current) {
                objectUrlRef.current = '';
            }

            await refresh();

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3500);
        } catch (err) {
            setSaveError(err?.response?.data?.message || 'Gagal memperbarui profil');
        } finally {
            setLoading(false);
        }
    };

    if (!profile) {
        return (
            <div className="pp-loading">
                <div className="pp-loading__spinner" />
                <span>Memuat profil…</span>
            </div>
        );
    }

    const initials = (profile.name || '')
        .split(' ')
        .slice(0, 2)
        .map((n) => n.charAt(0).toUpperCase())
        .join('');

    const isActive = profile.status === 'active';

    return (
        <div className="pp-root">
            <header className="pp-page-header">
                <div>
                    <h1 className="pp-page-title">Profil Saya</h1>
                    <p className="pp-page-sub">Kelola informasi akun dan profil Anda.</p>
                </div>
                <span
                    className={`pp-status-badge ${isActive ? 'pp-status-badge--active' : 'pp-status-badge--inactive'}`}
                >
                    <span className="pp-status-dot" />
                    {isActive ? 'Aktif' : 'Nonaktif'}
                </span>
            </header>

            <div className="pp-layout">
                <aside className="pp-sidebar">
                    <div className="pp-avatar-card">
                        <div className="pp-avatar-wrap">
                            <div className="pp-avatar">
                                {avatarPreview ? (
                                    <img src={avatarPreview} alt={profile.name} />
                                ) : (
                                    <span>{initials}</span>
                                )}
                            </div>
                            <button
                                type="button"
                                className="pp-avatar-overlay"
                                onClick={openFilePicker}
                                aria-label="Ganti foto profil"
                            >
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                    <circle cx="12" cy="13" r="4" />
                                </svg>
                            </button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            hidden
                            onChange={handleAvatarChange}
                        />

                        <div className="pp-avatar-info">
                            <p className="pp-avatar-name">{profile.name}</p>
                            <p className="pp-avatar-email">{profile.email}</p>
                        </div>
                    </div>
                    <div className="pp-plan-card">
                        <div className="pp-plan-icon">
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                        </div>
                        <div>
                            <p className="pp-plan-label">Paket saat ini</p>
                            <p className="pp-plan-name">{profile?.subscription?.plan?.name || 'Free'}</p>
                        </div>
                    </div>
                </aside>

                <main className="pp-main">
                    <form onSubmit={handleSubmit} className="pp-form">
                        <section className="pp-section">
                            <div className="pp-section-header">
                                <h2 className="pp-section-title">Informasi Pribadi</h2>
                                <p className="pp-section-desc">Nama dan kontak yang ditampilkan di akun Anda.</p>
                            </div>
                            <div className="pp-grid pp-grid--2">
                                <div className="pp-field">
                                    <label className="pp-label">Nama Lengkap</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className="pp-input"
                                        placeholder="Masukkan nama lengkap"
                                    />
                                </div>
                                <div className="pp-field">
                                    <label className="pp-label">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        className="pp-input"
                                        placeholder="email@domain.com"
                                    />
                                </div>
                                <div className="pp-field">
                                    <label className="pp-label">
                                        Nomor WhatsApp
                                        <span className="pp-label-badge">Tidak dapat diubah</span>
                                    </label>
                                    <input
                                        value={form.phone_number}
                                        disabled
                                        className="pp-input pp-input--disabled"
                                        placeholder="—"
                                    />
                                </div>
                            </div>
                        </section>

                        <div className="pp-divider" />

                        <section className="pp-section">
                            <div className="pp-section-header">
                                <h2 className="pp-section-title">Bio</h2>
                                <p className="pp-section-desc">Ceritakan sedikit tentang diri Anda.</p>
                            </div>
                            <div className="pp-field">
                                <textarea
                                    rows={4}
                                    name="bio"
                                    value={form.bio}
                                    onChange={handleChange}
                                    className="pp-input pp-textarea"
                                    placeholder="Tuliskan bio singkat Anda…"
                                />
                            </div>
                        </section>

                        <div className="pp-divider" />

                        <section className="pp-section">
                            <div className="pp-section-header">
                                <h2 className="pp-section-title">Alamat</h2>
                                <p className="pp-section-desc">Informasi lokasi yang terkait dengan akun Anda.</p>
                            </div>
                            <div className="pp-grid pp-grid--3">
                                <div className="pp-field">
                                    <label className="pp-label">Kota</label>
                                    <input
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        className="pp-input"
                                        placeholder="Nama kota"
                                    />
                                </div>
                                <div className="pp-field">
                                    <label className="pp-label">Provinsi</label>
                                    <input
                                        name="province"
                                        value={form.province}
                                        onChange={handleChange}
                                        className="pp-input"
                                        placeholder="Nama provinsi"
                                    />
                                </div>
                                <div className="pp-field">
                                    <label className="pp-label">Kode Pos</label>
                                    <input
                                        name="postal_code"
                                        value={form.postal_code}
                                        onChange={handleChange}
                                        className="pp-input"
                                        placeholder="00000"
                                    />
                                </div>
                            </div>
                            <div className="pp-field" style={{ marginTop: '16px' }}>
                                <label className="pp-label">Alamat Lengkap</label>
                                <textarea
                                    rows={3}
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    className="pp-input pp-textarea"
                                    placeholder="Jl. Nama Jalan No. XX, Kelurahan, Kecamatan…"
                                />
                            </div>
                        </section>

                        <div className="pp-form-footer">
                            <div className="pp-feedback-wrap">
                                {saveSuccess && (
                                    <div className="pp-feedback pp-feedback--success">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        Profil berhasil diperbarui.
                                    </div>
                                )}
                                {saveError && (
                                    <div className="pp-feedback pp-feedback--error">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        {saveError}
                                    </div>
                                )}
                            </div>

                            <button type="submit" disabled={loading} className="pp-save-btn">
                                {loading ? (
                                    <>
                                        <span className="pp-btn-spinner" />
                                        Menyimpan…
                                    </>
                                ) : (
                                    'Simpan Perubahan'
                                )}
                            </button>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
};

export default ProfilePage;
